import crypto from "crypto";
import {NIKETEAM_NMP_BASEURL, NIKETEAM_NMP_SIGNER} from "../../../config/envConstants";
import {calculateEndpointType} from "../../api/config/common";

const env = calculateEndpointType(window.location, 'prod')

interface ImageEdits {
    flatten?: { background: string };
    unflatten?: object;
    format?: string; // jpeg|png|webp|avif|tiff|gif|heic
    jpeg?: {
        quality: number;
        force: boolean;
    };
    png?: {
        force: boolean;
    };
    resize?: {
        width?: number | null;
        height?: number | null;
        fit?: string; // cover|contain|fill|inside|outside
    };
}

interface ImageRequest {
    outputFormat?: string;
    edits?: ImageEdits;
}

/**
 * Generates URL for image in Nike Media Platform (NMP) system
 *
 * Documentation:
 *      SharpJS: https://sharp.pixelplumbing.com/api-operation/
 *          - Formatting: https://sharp.pixelplumbing.com/api-output/
 *          - Resizing: https://sharp.pixelplumbing.com/api-resize/
 *      Nike Media Platform: https://github.com/nike-dee/media-platform
 *          - Note: edits:{} object supports key/value equivalent of SharpJS functions
 *
 * @param {string} imagePath - relative path to image in source bucket
 * @param settings
 * @returns {string} - generated URL
 */
export const generateNmpImageUrl = (imagePath:string, settings:ImageRequest):string => {
    const encodedImageRequest = btoa(JSON.stringify(settings))
    const pathToSign = `${imagePath}?m=${encodedImageRequest}`
    const signature = crypto.createHmac('sha256', NIKETEAM_NMP_SIGNER).update(pathToSign).digest('hex')
    return `${NIKETEAM_NMP_BASEURL}${pathToSign}&s=${signature}`
}

// Gets Product image URL in JPEG format
export const getProductImageUrl = (fileName:string, width:number, height:number, fit:string='inside', quality:number=80) => {
    const path = `/DPILS/${fileName}`;
    const imageSettings = {
        outputFormat: 'jpeg',
        edits: {
            flatten: { background: '#ffffff' },
            jpeg: {
                quality: quality,
                force: true
            },
            ...((width || height) && {
                resize: {
                    ...(width && { width }),
                    ...(height && { height }),
                    fit: fit,
                    background: '#ffffff'
                }
            })
        }
    }
    return generateNmpImageUrl(path, imageSettings)
}

// Gets Artwork/logo image URL in PNG format
export const getArtworkImageUrl = (fileName:string, width:number, height:number, fit:string='inside', quality:number=80):string => {
    const path = `/artwork/${env}/${fileName}`;
    const imageSettings = {
        outputFormat: 'png',
        unflatten: {},
        edits: {
            png: {
                quality: quality,
                force: true
            },
            ...((width || height) && {
                resize: {
                    ...(width && { width }),
                    ...(height && { height }),
                    fit: fit,
                }
            })
        }
    }
    return generateNmpImageUrl(path, imageSettings)
}

// Gets Graphic image URL in PNG format
export const getGraphicImageUrl = (fileName:string, width:number, height:number, fit:string='inside'):string => {
    const path = `/graphic/${fileName}`;
    const imageSettings = {
        outputFormat: 'png',
        unflatten: {},
        edits: {
            png: {
                quality: 100,
                force: true
            },
            ...((width || height) && {
                resize: {
                    ...(width && { width }),
                    ...(height && { height }),
                    fit: fit
                }
            })
        }
    }
    return generateNmpImageUrl(path, imageSettings)
}
